<template>
  <div class="relative spotlight-wrapper" :class="{ 'spotlight-ring': visible }">
    <div @click="onTargetClick">
      <slot />
    </div>

    <Transition name="spotlight-callout">
      <div v-if="visible" class="spotlight-card absolute z-50 w-72 bg-white border border-blue-200 rounded-xl shadow-lg overflow-hidden"
        :class="positionClasses">
        <div class="bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-2.5 flex items-center gap-2">
          <i v-if="icon" :class="icon" class="text-white text-sm"></i>
          <span class="text-white text-sm font-semibold">{{ title }}</span>
        </div>
        <div class="p-4 text-sm text-gray-700">
          <slot name="content" />
        </div>
        <div class="px-4 pb-3 flex justify-end">
          <Button :label="dismissLabel" size="small" @click="dismiss" class="!px-4" />
        </div>
        <div class="spotlight-arrow" :class="arrowClasses"></div>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { useLocalStorage } from '@vueuse/core';

type Position = 'bottom-right' | 'bottom-left' | 'bottom-center';

const props = withDefaults(defineProps<{
  storageKey: string;
  title: string;
  icon?: string;
  position?: Position;
  delay?: number;
  dismissLabel?: string;
  dismissOnTargetClick?: boolean;
}>(), {
  icon: 'pi pi-sparkles',
  position: 'bottom-right',
  delay: 1500,
  dismissLabel: 'Got it',
  dismissOnTargetClick: true,
});

const emit = defineEmits<{
  dismiss: [];
}>();

const dismissed = useLocalStorage(props.storageKey, false);
const visible = ref(false);

onMounted(() => {
  if (!dismissed.value) {
    setTimeout(() => { visible.value = true; }, props.delay);
  }
});

const dismiss = () => {
  visible.value = false;
  dismissed.value = true;
  emit('dismiss');
};

const onTargetClick = () => {
  if (props.dismissOnTargetClick && visible.value) {
    dismiss();
  }
};

const positionClasses = computed(() => {
  switch (props.position) {
  case 'bottom-left': return 'top-full left-0 mt-2';
  case 'bottom-center': return 'top-full left-1/2 -translate-x-1/2 mt-2';
  case 'bottom-right':
  default: return 'top-full right-0 mt-2';
  }
});

const arrowClasses = computed(() => {
  switch (props.position) {
  case 'bottom-left': return 'spotlight-arrow-left';
  case 'bottom-center': return 'spotlight-arrow-center';
  case 'bottom-right':
  default: return 'spotlight-arrow-right';
  }
});

defineExpose({ dismiss, visible });
</script>

<style scoped>
.spotlight-ring {
  border-radius: 50%;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4);
  animation: spotlight-pulse 2s ease-in-out infinite;
}

@keyframes spotlight-pulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.4); }
  50% { box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.15); }
}

.spotlight-arrow {
  position: absolute;
  top: -6px;
  width: 12px;
  height: 12px;
  background: white;
  border-top: 1px solid #bfdbfe;
  border-left: 1px solid #bfdbfe;
  transform: rotate(45deg);
}

.spotlight-arrow-right { right: 16px; }
.spotlight-arrow-left { left: 16px; }
.spotlight-arrow-center { left: 50%; margin-left: -6px; }

.spotlight-callout-enter-active {
  animation: spotlight-fade-in 0.3s ease-out;
}
.spotlight-callout-leave-active {
  animation: spotlight-fade-in 0.2s ease-in reverse;
}

@keyframes spotlight-fade-in {
  0% { opacity: 0; transform: translateY(-8px); }
  100% { opacity: 1; transform: translateY(0); }
}
</style>
